import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ICuriosity } from '@core/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CdnUrlPipe } from '@shared/pipes';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Authentication, Storage, User } from '@core/services';
import { lastValueFrom } from 'rxjs';
import { STORAGE_KEYS_CONSTANTS } from '@core/constants';
import Swal from 'sweetalert2';

/**
 * ### **CuriositiesComponent**
 *
 * **Selector:** ``<app-curiosities>``
 *
 * Un componente es un bloque, que contiene un template, contiene estilos y contiene la lógica, en este caso es el Componente
 * encargado de renderizar las curiosidades que le son enviadas por parametro.
 */
@Component({
  selector: 'app-curiosities',
  imports: [TranslateModule, ReactiveFormsModule, LazyLoadImageModule, CdnUrlPipe],
  templateUrl: "./curiosities.component.html",
  styleUrl: "./curiosities.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Curiosities implements OnInit {
  private readonly router = inject(Router);
  private readonly userService = inject(User);
  private readonly auth = inject(Authentication);
  private readonly translate = inject(TranslateService);
  private readonly storage = inject(Storage);
  public readonly dialogRef = inject(MatDialogRef<Curiosities>);
  public readonly data: {
    curiosity?: ICuriosity;
    curiosities?: Array<ICuriosity>;
    many?: boolean;
  } = inject(MAT_DIALOG_DATA);

  /** Variable que guardará una curiosidad */
  protected readonly curiosity = signal<ICuriosity | null>(null);
  /** Variable que guardara la lista de curiosidades */
  protected readonly curiosities = signal<ICuriosity[]>([]);
  /** Variable que guardara el estado que hace referencia a la visualización del componente*/
  protected readonly showControl = signal<FormControl>(new FormControl(false));
  /** Variale que guarda el valor que determina si son muchas curiosidades*/
  protected readonly many = signal<boolean>(false);
  /** Variable que guarda el identificador de la pagina actual para las curiosidades*/
  protected readonly actualPage = signal<number>(0);
  /** Varible que guardará la información del autenticacion del usuario*/
  protected readonly user = this.auth.getAuthInformation();
  /** Variable que guardará un mensaje para el usuario*/
  message!: string;

  constructor() {
    if (this.data.curiosity) {
      this.curiosity.set(this.data.curiosity);
    }

    if (this.data.curiosities) {
      this.curiosities.set(this.data.curiosities);
      this.many.set(true);
      this.actualPage.set(0);
    }
    if (this.user) {
      this.showControl().setValue(!this.user.show_curiosity);
    }

    this.showControl()
      .valueChanges.pipe(takeUntilDestroyed())
      .subscribe((value) => {
        this.setCuriosityStorage(!value);
        if (this.user) {
          this.updateShowCuriosityUser(!value);
        }
      });
  }

  /**
   * Funcion asincrona encargada de actualizar el valor de visibilidad de curiosidades para el usuario.
   * @param value Estado de la visibilidad de las curiosidades.
   */
  private async updateShowCuriosityUser(value: boolean): Promise<void> {
    await lastValueFrom(this.userService.edit(Number(this.user?.id), { show_curiosity: value }));
  }

  /**
   * Función nativa de angular, esta es llamada despues del constructor, aqui se inicializa la variable
   * message, la cua se vera reflejada al usuario en caso de querer acceder a mas curiosidades y no
   * haber iniciado sesión.
   */
  ngOnInit() {
    this.message = this.translate.instant('more_curiosities_no_logged');
  }

  /**
   * Función encargada de enviar el estado resferente a la visibilidad al almacenamiento local.
   * @param value Estado de la visibilidad de las curiosidades.
   */
  private setCuriosityStorage(value: any): void {
    this.storage.setSession(STORAGE_KEYS_CONSTANTS.CURIOSITY_TOKEN, value);
  }

  /**
   * Función encargada de cambiar a la curiosidad por otra que se encuentre a la derecha de la misma.
   */
  protected right(): void {
    if (this.curiosities().length === 0) return;
    this.actualPage.update((value) => (value + 1) % this.curiosities().length);
  }

  /**
   * Función encargada de cambiar a la curiosidad por otra que se encuentre a la izquierda de la misma
   */
  protected left(): void {
    if (this.curiosities().length === 0) return;
    this.actualPage.update((value) => (value === 0 ? this.curiosities().length - 1 : value - 1));
  }

  /**
   * @description
   * Este metodo es el encargado de redireccionar al usuario a su perfil en caso de que este quiera ver más curiosidades,
   * sin embargo esto solo lo hara si este se encuentra logueado en el sistema, en caso contrario le notificara que tiene
   * que estar logueado y luego le abrira el pop up de inicio de sesión.
   */
  protected moreCuriosities(): void {
    if (this.user) {
      this.router.navigate(['profile'], {
        queryParams: {
          moreCuriosities: 1,
        },
      });
    } else {
      Swal.fire({
        text: this.message,
        icon: 'warning',
        confirmButtonAriaLabel: 'Ok',
      }).then(() => {
        this.auth.openLoginForm.next(true);
      });
    }
  }
}
